import React, { useState, useMemo, useRef, useEffect } from "react";
import debounce from "lodash/debounce";
import {
    Box,
    Autocomplete,
    TextField,
    Chip,
    Paper,
    Typography,
    Divider,
    CircularProgress,
    Switch,
    FormControlLabel,
    Button,
    Alert,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
} from "@mui/material";
import {
    useLazySearchShipmentWithPrimaryStatusQuery,
    useUpdateShipmentPrimaryStatusMutation,
} from "store/services/shipmentApi";
import { useAuth } from "hooks";
import NotFoundScreen from "screens/NotFoundScreen";
const STATUS_FIELDS = [
    { name: "isActive", label: "Active" },
    { name: "isOnHold", label: "On Hold" },
    { name: "isConsolidated", label: "Consolidated" },
    { name: "IsReturning", label: "Returning" },
    { name: "isMisplaced", label: "Misplaced" },
];
const SHIPMENT_STATUS_OPTIONS = [
    "Pending",
    "Collected",
    "Consolidation",
    "Loaded",
    "Unloaded",
    "Deconsolidation",
    "CrossDocked",
    "SendOut",
    "Warehousing",
    "Delivered",
    "DeliveryFailed",
    "Return",
    "Cancelled",
];
const MIN_SEARCH_LENGTH = 4;
const ShipmentPrimaryStatusScreen = () => {
    const { isAccountAdmin } = useAuth();
    const [selectedShipment, setSelectedShipment] = useState(null);
    const [resetKey, setResetKey] = useState(0);
    const [statusValues, setStatusValues] = useState(null);
    const [description, setDescription] = useState("");
    const [descriptionError, setDescriptionError] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [triggerSearch, { data: shipments, isFetching: loadingShipments }] =
        useLazySearchShipmentWithPrimaryStatusQuery();
    const [updatePrimaryStatus, { isLoading: isSaving }] =
        useUpdateShipmentPrimaryStatusMutation();
    const handleReset = () => {
        setSelectedShipment(null);
        setStatusValues(null);
        setDescription("");
        setDescriptionError("");
        setError("");
        setSuccess("");
        setResetKey((k) => k + 1);
    };
    const triggerRef = useRef(triggerSearch);
    useEffect(() => {
        triggerRef.current = triggerSearch;
    }, [triggerSearch]);
    const debouncedSearch = useMemo(
        () =>
            debounce((term) => {
                const cleaned = term?.replace(/-/g, "") ?? "";
                if (!cleaned || cleaned.length < MIN_SEARCH_LENGTH) return;
                triggerRef.current({ query: cleaned });
            }, 400),
        []
    );
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);
    const handleInputChange = (_, newInput, reason) => {
        if (reason === "reset") return;
        debouncedSearch(newInput);
    };
    const handleSelect = (_, shipment) => {
        setSelectedShipment(shipment);
        setError("");
        setSuccess("");
        setDescription("");
        setDescriptionError("");
        setStatusValues(
            shipment
                ? {
                    isActive: !!shipment.isActive,
                    isOnHold: !!shipment.isOnHold,
                    isConsolidated: !!shipment.isConsolidated,
                    IsReturning: !!shipment.IsReturning,
                    isMisplaced: !!shipment.isMisplaced,
                    status: shipment.status || "Pending",
                }
                : null
        );
    };
    const handleToggle = (name) => (e) => {
        setSuccess("");
        setStatusValues((prev) => ({ ...prev, [name]: e.target.checked }));
    };
    const handleStatusChange = (e) => {
        setSuccess("");
        setStatusValues((prev) => ({ ...prev, status: e.target.value }));
    };
    const handleDescriptionChange = (e) => {
        setSuccess("");
        setDescription(e.target.value);
        if (e.target.value.trim()) setDescriptionError("");
    };
    const isDirty =
        selectedShipment &&
        statusValues &&
        (STATUS_FIELDS.some(
            ({ name }) => !!selectedShipment[name] !== !!statusValues[name]
        ) ||
            (selectedShipment.status || "Pending") !== statusValues.status);
    const handleSave = async () => {
        if (!selectedShipment || !statusValues) return;
        if (!description.trim()) {
            setDescriptionError("Description is required");
            return;
        }
        setError("");
        setSuccess("");
        try {
            const result = await updatePrimaryStatus({
                id: selectedShipment._id,
                ...statusValues,
                description: description.trim(),
            }).unwrap();
            setSelectedShipment(result.data);
            setDescription("");
            setSuccess("Status updated successfully.");
        } catch (err) {
            setError(err?.data?.message || "Failed to update status.");
        }
    };
    if (!isAccountAdmin) {
        return <NotFoundScreen />;
    }
    return (
        <Paper sx={{ p: 3, maxWidth: 480 }}>
            <Typography variant="h6" gutterBottom>
                Shipment Status Lookup
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            )}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Autocomplete
                key={resetKey}
                options={shipments?.data || []}
                filterOptions={(x) => x}
                loading={loadingShipments}
                value={selectedShipment}
                onChange={handleSelect}
                onInputChange={handleInputChange}
                getOptionLabel={(opt) => opt.shipmentId || ""}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Search Shipment"
                        placeholder="Type at least 4 characters..."
                        variant="outlined"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loadingShipments ? <CircularProgress size={18} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
            {selectedShipment && (
                <>
                    {/* Current status - read only view */}
                    <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                        Current Status
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                        <Chip
                            label={`Status: ${selectedShipment.status || "Pending"}`}
                            color="info"
                            variant="filled"
                        />
                        {STATUS_FIELDS.map(({ name, label }) => (
                            <Chip
                                key={name}
                                label={`${label}: ${selectedShipment[name] ? "On" : "Off"}`}
                                color={selectedShipment[name] ? "success" : "default"}
                                variant={selectedShipment[name] ? "filled" : "outlined"}
                            />
                        ))}
                    </Box>
                    {isAccountAdmin && (
                        <>
                            <Divider sx={{ mb: 2 }} />
                            {/* Editable toggles */}
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Update Status
                            </Typography>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel id="shipment-status-label">Status</InputLabel>
                                <Select
                                    labelId="shipment-status-label"
                                    label="Status"
                                    value={statusValues?.status || "Pending"}
                                    onChange={handleStatusChange}
                                >
                                    {SHIPMENT_STATUS_OPTIONS.map((opt) => (
                                        <MenuItem key={opt} value={opt}>
                                            {opt}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                {STATUS_FIELDS.map(({ name, label }) => (
                                    <FormControlLabel
                                        key={name}
                                        control={
                                            <Switch
                                                checked={!!statusValues?.[name]}
                                                onChange={handleToggle(name)}
                                            />
                                        }
                                        label={label}
                                    />
                                ))}
                            </Box>
                            <TextField
                                fullWidth
                                required
                                multiline
                                rows={3}
                                size="small"
                                label="Reason for Change"
                                placeholder="Why is this status being changed?"
                                value={description}
                                onChange={handleDescriptionChange}
                                error={!!descriptionError}
                                helperText={descriptionError}
                                sx={{ mt: 2 }}
                            />
                            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleSave}
                                    disabled={isSaving || !isDirty}
                                >
                                    {isSaving ? "Saving..." : "Save Status"}
                                </Button>
                                {success && (
                                    <Button variant="outlined" onClick={handleReset}>
                                        New
                                    </Button>
                                )}
                            </Box>
                        </>
                    )}
                </>
            )}
        </Paper>
    );
};
export default ShipmentPrimaryStatusScreen;